import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import ExploreIcon from "@mui/icons-material/Explore";
import StorefrontIcon from "@mui/icons-material/Storefront";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import PlaceIcon from "@mui/icons-material/Place";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import StarIcon from "@mui/icons-material/Star";
import VerifiedIcon from "@mui/icons-material/Verified";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import { useProjectConfig } from "../context/ProjectConfigContext";
import { useLanding } from "../context/LandingContext";
import {
  handleLandingMediaError,
  resolveLandingMedia,
  TRANSPARENT_MEDIA_FALLBACK,
} from "../utils/landingMedia";
import { openConfiguredUrl } from "../utils/configuredNavigation";
import { navbarOffsetSx, resolveNavbarStyle } from "../utils/landingLayout";
import { catalogLabel, catalogListPath, normalizeCatalogMode } from "../utils/catalogRouting";
import {
  getSegmentVisualStyle,
  scaleResponsiveCss,
  segmentVisualScale,
} from "../utils/segmentVisualStyle";
import {
  heroPhraseFontSize,
  heroSubtitleFontSize,
  heroTitleFontSize,
  safeButtonTextSx,
  safeTextSx,
  safeTitleSx,
  textLength,
} from "../utils/textSafety";

const HERO_CTA_ICON_COMPONENTS = Object.freeze({
  explore: ExploreIcon,
  storefront: StorefrontIcon,
  whatsapp: WhatsAppIcon,
  phone: PhoneIcon,
  mail: EmailIcon,
  place: PlaceIcon,
  shopping: ShoppingBagIcon,
  event: EventAvailableIcon,
  star: StarIcon,
  verified: VerifiedIcon,
  autoawesome: AutoAwesomeIcon,
  arrow: ArrowForwardIcon,
});

function HeroCtaIcon({ name = "", fallback = "explore" }) {
  const Icon = HERO_CTA_ICON_COMPONENTS[String(name || "").trim()] || HERO_CTA_ICON_COMPONENTS[fallback];
  return Icon ? <Icon /> : null;
}

const responsive = {
  desktop: { breakpoint: { max: 4000, min: 1024 }, items: 1 },
  tablet: { breakpoint: { max: 1024, min: 600 }, items: 1 },
  mobile: { breakpoint: { max: 600, min: 0 }, items: 1 },
};

const DEFAULT_HERO_OVERLAY =
  "linear-gradient(to bottom, rgba(0,0,0,.18), rgba(8,8,20,.72))";

function bounded(value, fallback, min, max) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.min(max, Math.max(min, number));
}

function normalizeSliderAnimation(value) {
  const normalized = String(value || "slide").trim().toLowerCase();
  return ["slide", "fade", "zoom", "none"].includes(normalized) ? normalized : "slide";
}

function handleHeroMediaError(event, fallbackCandidates = []) {
  const element = event?.currentTarget;
  if (!element) return;

  const currentSource = String(element.currentSrc || element.src || "");
  const tried = new Set(String(element.dataset.wpHeroMediaTried || "").split("\n").filter(Boolean));
  tried.add(currentSource);

  const nextSource = fallbackCandidates
    .map((candidate) => resolveLandingMedia(candidate))
    .find((candidate) => candidate && !tried.has(candidate));

  if (nextSource) {
    tried.add(nextSource);
    element.dataset.wpHeroMediaTried = Array.from(tried).join("\n");
    element.removeAttribute("srcset");
    element.src = nextSource;
    return;
  }

  handleLandingMediaError(event, TRANSPARENT_MEDIA_FALLBACK);
}

function normalizeHeroHorizontalPosition(value) {
  const candidate = String(value || "").trim().toLowerCase();
  if (["right", "derecha", "end", "flex-end"].includes(candidate)) return "right";
  if (["center", "centre", "centro"].includes(candidate)) return "center";
  return "left";
}

function normalizeHeroVerticalPosition(value) {
  const candidate = String(value || "").trim().toLowerCase();
  if (["top", "arriba", "start", "flex-start"].includes(candidate)) return "top";
  if (["bottom", "abajo", "end", "flex-end"].includes(candidate)) return "bottom";
  return "center";
}

function heroHorizontalFlex(value) {
  if (value === "right") return "flex-end";
  if (value === "center") return "center";
  return "flex-start";
}

function heroVerticalFlex(value) {
  if (value === "top") return "flex-start";
  if (value === "bottom") return "flex-end";
  return "center";
}

function heroBlockMargins(value) {
  if (value === "right") return { ml: "auto", mr: 0 };
  if (value === "center") return { ml: "auto", mr: "auto" };
  return { ml: 0, mr: "auto" };
}

function finiteStyleNumber(value, fallback) {
  if (value === "" || value === null || value === undefined) return fallback;
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function cssColorWithOpacity(color, opacity = 1) {
  const candidate = String(color || "").trim();
  const alpha = Math.max(0, Math.min(1, finiteStyleNumber(opacity, 1)));
  if (!candidate || candidate.toLowerCase() === "transparent" || alpha <= 0) return "transparent";
  if (alpha >= 0.999) return candidate;

  const hex = candidate.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const raw = hex[1].length === 3
      ? hex[1].split("").map((char) => `${char}${char}`).join("")
      : hex[1];
    const red = parseInt(raw.slice(0, 2), 16);
    const green = parseInt(raw.slice(2, 4), 16);
    const blue = parseInt(raw.slice(4, 6), 16);
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  }

  return `color-mix(in srgb, ${candidate} ${Math.round(alpha * 1000) / 10}%, transparent)`;
}

function heroCtaButtonSx(style = {}, { outlined = false, colors = {} } = {}) {
  const currentStyle = style && typeof style === "object" ? style : {};
  const primary = String(colors.primary || "").trim() || "#F35A3A";
  const secondary = String(colors.secondary || "").trim() || "#19242F";
  const onPrimary = String(colors.onPrimary || "").trim() || "#ffffff";
  const onSecondary = String(colors.onSecondary || "").trim() || "#ffffff";
  const backgroundColor = String(currentStyle.backgroundColor || "").trim() || (outlined ? "#ffffff" : primary);
  const hoverBackgroundColor = String(currentStyle.hoverBackgroundColor || "").trim() || (outlined ? "#ffffff" : secondary);
  const textColor = String(currentStyle.textColor || "").trim() || (outlined ? onSecondary : onPrimary);
  const hoverTextColor = String(currentStyle.hoverTextColor || "").trim() || textColor;
  const borderColor = String(currentStyle.borderColor || "").trim() || onSecondary;
  const borderWidth = finiteStyleNumber(currentStyle.borderWidth, outlined ? 1.5 : 0);
  const borderRadius = finiteStyleNumber(currentStyle.borderRadius, 999);
  const backgroundOpacity = finiteStyleNumber(currentStyle.backgroundOpacity, outlined ? 0.08 : 1);
  const hoverBackgroundOpacity = finiteStyleNumber(currentStyle.hoverBackgroundOpacity, outlined ? 0.18 : 1);
  const shadowColor = String(currentStyle.shadowColor || "").trim() || "#000000";
  const shadowOpacity = finiteStyleNumber(currentStyle.shadowOpacity, outlined ? 0 : 0.28);
  const shadowX = finiteStyleNumber(currentStyle.shadowX, 0);
  const shadowY = finiteStyleNumber(currentStyle.shadowY, outlined ? 0 : 10);
  const shadowBlur = finiteStyleNumber(currentStyle.shadowBlur, outlined ? 0 : 28);
  const shadowSpread = finiteStyleNumber(currentStyle.shadowSpread, 0);
  const minWidth = finiteStyleNumber(currentStyle.minWidth, null);
  const minHeight = finiteStyleNumber(currentStyle.minHeight, null);
  const paddingX = finiteStyleNumber(currentStyle.paddingX, null);
  const paddingY = finiteStyleNumber(currentStyle.paddingY, null);
  const fontSize = finiteStyleNumber(currentStyle.fontSize, null);
  const fontWeight = finiteStyleNumber(currentStyle.fontWeight, null);
  const boxShadow = shadowOpacity <= 0
    ? "none"
    : `${shadowX}px ${shadowY}px ${shadowBlur}px ${shadowSpread}px ${cssColorWithOpacity(shadowColor, shadowOpacity)}`;

  return {
    ...safeButtonTextSx,
    width: { xs: "100%", sm: "auto" },
    minWidth: minWidth === null ? { xs: 0, sm: 190 } : `${minWidth}px`,
    maxWidth: "100%",
    minHeight: minHeight === null ? { xs: 46, sm: 48 } : `${minHeight}px`,
    px: paddingX === null ? { xs: 2.4, sm: 3.5 } : `${paddingX}px`,
    py: paddingY === null ? 1.2 : `${paddingY}px`,
    borderRadius: `${borderRadius}px`,
    borderWidth: `${borderWidth}px`,
    borderStyle: borderWidth > 0 ? "solid" : "none",
    borderColor,
    color: textColor,
    bgcolor: cssColorWithOpacity(backgroundColor, backgroundOpacity),
    backdropFilter: "blur(10px)",
    boxShadow,
    ...(fontSize === null ? {} : { fontSize: `${fontSize}px` }),
    ...(fontWeight === null ? {} : { fontWeight }),
    "& .MuiButton-startIcon": {
      flexShrink: 0,
      alignSelf: "center",
    },
    "&:hover": {
      borderColor: String(currentStyle.borderColor || "").trim() || "#ffffff",
      color: hoverTextColor,
      bgcolor: cssColorWithOpacity(hoverBackgroundColor, hoverBackgroundOpacity),
    },
  };
}

function HeroButton({ label, url, outlined = false, icon, disabled, fieldPath, style, colors }) {
  const navigate = useNavigate();
  if (!label || !url || disabled) return null;

  return (
    <Button
      data-landing-field={fieldPath || undefined}
      variant={outlined ? "outlined" : "contained"}
      startIcon={icon}
      onClick={() => openConfiguredUrl(url, navigate)}
      sx={heroCtaButtonSx(style, { outlined, colors })}
    >
      {label}
    </Button>
  );
}

export default function PromotionsCarousel({ sectionDomId = "hero", sectionBackgroundSx = {}, segmentVisualStyle = {} }) {
  const { productsEnabled } = useProjectConfig();
  const muiTheme = useMuiTheme();
  const { hero, theme, business, navigation, sections, catalog } = useLanding();
  const navbarOffset = navbarOffsetSx(resolveNavbarStyle(navigation, theme), sections?.showNavbar !== false);
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [phase, setPhase] = useState("visible");
  const phraseTimeout = useRef(null);

  const phraseEntries = useMemo(
    () => (Array.isArray(hero?.rotatingPhrases)
      ? hero.rotatingPhrases
          .map((value, sourceIndex) => ({ value, sourceIndex }))
          .filter((entry) => Boolean(entry.value))
      : []),
    [hero?.rotatingPhrases]
  );
  const phrases = phraseEntries.map((entry) => entry.value);

  const slides = useMemo(() => {
    const configured = Array.isArray(hero?.slides)
      ? hero.slides
          .map((slide, sourceIndex) => ({ ...slide, __sourceIndex: sourceIndex }))
          .filter((slide) => slide?.url)
      : [];

    return configured.length
      ? configured
      : [
          {
            url: hero?.imageUrl,
            alt: hero?.title ?? business?.name ?? "",
            title: hero?.title,
            subtitle: hero?.subtitle,
            position: hero?.imagePosition,
            __sourceIndex: null,
          },
        ];
  }, [hero, business?.name]);

  useEffect(() => {
    if (phrases.length < 2) return undefined;

    const interval = window.setInterval(() => {
      setPhase("exit");
      window.clearTimeout(phraseTimeout.current);
      phraseTimeout.current = window.setTimeout(() => {
        setPhraseIndex((current) => (current + 1) % phrases.length);
        setPhase("enter");
        window.requestAnimationFrame(() =>
          window.requestAnimationFrame(() => setPhase("visible"))
        );
      }, 380);
    }, 3400);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(phraseTimeout.current);
    };
  }, [phrases.length]);

  const heroBackgroundStart = "#494A98";
  const heroBackgroundEnd = "#E44E31";
  const heroHorizontalPosition = normalizeHeroHorizontalPosition(
    hero?.contentHorizontalAlign ??
      hero?.contentHorizontalPosition ??
      hero?.textHorizontalPosition ??
      hero?.contentPositionHorizontal ??
      hero?.horizontalPosition
  );
  const heroVerticalPosition = normalizeHeroVerticalPosition(
    hero?.contentVerticalAlign ??
      hero?.contentVerticalPosition ??
      hero?.textVerticalPosition ??
      hero?.contentPositionVertical ??
      hero?.verticalPosition
  );
  const heroContentJustify = heroHorizontalFlex(heroHorizontalPosition);
  const heroContentAlign = heroVerticalFlex(heroVerticalPosition);
  const heroTextAlign = heroHorizontalPosition;
  const heroContentMargins = heroBlockMargins(heroHorizontalPosition);
  const heroHorizontalPadding = {
    xs: "21.12px",
    sm: "42.24px",
    md: "63.36px",
  };
  const titleScale = segmentVisualScale(theme, "hero", "title");
  const heroVisualStyle = getSegmentVisualStyle(theme, "hero");
  const eyebrowSize = scaleResponsiveCss(muiTheme?.typography?.overline?.fontSize || ".75rem", 1);
  const imageScale = Number(heroVisualStyle?.imageSize) === 2
    ? 1
    : segmentVisualScale(theme, "hero", "image");
  const spacingScale = segmentVisualScale(theme, "hero", "spacing");
  const segmentBackgroundImage = resolveLandingMedia(segmentVisualStyle?.backgroundImageUrl);
  const globalHeroColors = {
    primary: theme?.primaryColor || "#F35A3A",
    secondary: theme?.secondaryColor || "#19242F",
    onPrimary: "#FFFFFF",
    onSecondary: "#FFFFFF",
  };
  const heroColors = segmentVisualStyle?.useGlobalColors === false
    ? { ...globalHeroColors, ...(segmentVisualStyle?.colors || {}) }
    : globalHeroColors;
  // La cadena vacía es una decisión explícita del usuario: portada sin capa.
  // Solo heredamos el gradiente por defecto si el campo no existe en datos antiguos.
  const effectiveHeroOverlay = hero?.overlay === undefined || hero?.overlay === null
    ? DEFAULT_HERO_OVERLAY
    : String(hero.overlay).trim();
  const rotatingPhrase = phrases[phraseIndex] || "";
  const catalogMode = normalizeCatalogMode(catalog?.mode);
  const secondaryCtaIsCatalog = ["/productos", "/servicios", "/catalogo"].includes(
    String(hero?.secondaryCtaUrl || "").trim(),
  );
  const secondaryCtaUrl = secondaryCtaIsCatalog
    ? catalogListPath(catalogMode, navigation?.productsUrl || "/productos")
    : hero?.secondaryCtaUrl;
  const secondaryCtaLabel =
    secondaryCtaIsCatalog &&
    /^ver productos(?: y servicios)?$/i.test(String(hero?.secondaryCtaLabel || "").trim())
      ? `Ver ${catalogLabel(catalogMode, navigation?.productsLabel).toLowerCase()}`
      : hero?.secondaryCtaLabel;
  const showPrimaryCta = Boolean(hero?.primaryCtaLabel && hero?.primaryCtaUrl) &&
    (!hero?.primaryCtaRequiresProducts || productsEnabled);
  const showSecondaryCta = Boolean(secondaryCtaLabel && secondaryCtaUrl) &&
    (!hero?.secondaryCtaRequiresProducts || productsEnabled);
  const sliderAnimation = normalizeSliderAnimation(hero?.animation);
  const sliderAutoplay = hero?.autoplay !== false && slides.length > 1;
  const sliderInterval = bounded(hero?.intervalMs, 6500, 2000, 15000);
  const sliderTransitionDuration = sliderAnimation === "slide" ? 850 : 0;
  const sliderCustomTransition = sliderAnimation === "slide" ? undefined : "transform 0ms linear";
  const activeSlideAnimation = sliderAnimation === "fade"
    ? "wpHeroSliderFadeIn 560ms ease both"
    : sliderAnimation === "zoom"
      ? "wpHeroSliderZoomIn 680ms ease-out both"
      : "none";

  return (
    <Box
      component="section"
      id={sectionDomId}
      data-landing-section="hero"
      sx={{
        position: "relative",
        "--wp-hero-min-height": { xs: "min(88svh, 720px)", sm: "max(100svh, 620px)" },
        minHeight: "var(--wp-hero-min-height)",
        overflow: "hidden",
        bgcolor: heroBackgroundStart,
        scrollMarginTop: navbarOffset,
        "& .react-multi-carousel-list": {
          minHeight: "var(--wp-hero-min-height)",
        },
        "& .react-multi-carousel-track": {
          minHeight: "var(--wp-hero-min-height)",
          alignItems: "stretch",
        },
        "& .react-multi-carousel-item": {
          display: "flex",
          minWidth: 0,
          minHeight: "var(--wp-hero-min-height)",
        },
        "& .react-multi-carousel-item > *": {
          width: "100%",
          minWidth: 0,
        },
        "& .react-multi-carousel-item--active > *": {
          animation: activeSlideAnimation,
          willChange: sliderAnimation === "none" || sliderAnimation === "slide" ? "auto" : "transform, opacity",
        },
        "@keyframes wpHeroSliderFadeIn": {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
        "@keyframes wpHeroSliderZoomIn": {
          from: { opacity: 0, transform: "scale(1.025)" },
          to: { opacity: 1, transform: "scale(1)" },
        },
        ...sectionBackgroundSx,
      }}
    >
      <Carousel
        responsive={responsive}
        infinite={slides.length > 1}
        autoPlay={sliderAutoplay}
        autoPlaySpeed={sliderInterval}
        transitionDuration={sliderTransitionDuration}
        customTransition={sliderCustomTransition}
        arrows={false}
        swipeable={slides.length > 1}
        draggable={slides.length > 1}
        ssr
      >
        {slides.map((slide, index) => {
          const image = resolveLandingMedia(slide?.url || hero?.imageUrl || segmentBackgroundImage);
          const title = slide?.title ?? hero?.title ?? business?.name ?? "";
          const subtitle = slide?.subtitle ?? hero?.subtitle ?? "";
          const slideSourceIndex = Number.isInteger(slide?.__sourceIndex) ? slide.__sourceIndex : null;
          const titleFieldPath = slideSourceIndex === null ? "hero.title" : `hero.slides.${slideSourceIndex}.title`;
          const subtitleFieldPath = slideSourceIndex === null ? "hero.subtitle" : `hero.slides.${slideSourceIndex}.subtitle`;
          const rotatingFieldPath = phraseEntries[phraseIndex]
            ? `hero.rotatingPhrases.${phraseEntries[phraseIndex].sourceIndex}`
            : "hero.rotatingPhrases.0";
          const titleSize = scaleResponsiveCss(
            heroTitleFontSize(title),
            titleScale
          );
          const phraseSize = scaleResponsiveCss(heroPhraseFontSize(rotatingPhrase), titleScale);
          const subtitleIsVeryLong = textLength(subtitle) > 420;
          const subtitleSize = scaleResponsiveCss(
            heroSubtitleFontSize(subtitle),
            1
          );

          return (
            <Box
              key={`${slide?.url || "hero"}-${index}`}
              sx={{
                position: "relative",
                display: "flex",
                width: "100%",
                minWidth: 0,
                minHeight: "var(--wp-hero-min-height)",
                background: `linear-gradient(135deg, ${heroBackgroundStart}, ${heroBackgroundEnd})`,
              }}
            >
              {image ? (
                <Box
                  component="img"
                  src={image}
                  alt={slide?.alt ?? title ?? business?.name ?? ""}
                  loading={index === 0 ? "eager" : "lazy"}
                  decoding="async"
                  onError={(event) =>
                    handleHeroMediaError(event, [hero?.imageUrl, segmentBackgroundImage])
                  }
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: slide?.position || hero?.imagePosition || "center",
                    transform: `scale(${imageScale})`,
                    transformOrigin: "center",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background: `linear-gradient(135deg, ${heroBackgroundStart}, ${heroBackgroundEnd})`,
                  }}
                />
              )}

              {effectiveHeroOverlay ? (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background: effectiveHeroOverlay,
                  }}
                />
              ) : null}

              <Box
                sx={{
                  position: "relative",
                  zIndex: 2,
                  display: "flex",
                  width: "100%",
                  minWidth: 0,
                  minHeight: "inherit",
                  alignItems: heroContentAlign,
                  justifyContent: heroContentJustify,
                  px: heroHorizontalPadding,
                  pt: { xs: 10, sm: 16, md: 18 },
                  pb: { xs: 5 * spacingScale, sm: 9 * spacingScale, md: 11 * spacingScale },
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 1120,
                    minWidth: 0,
                    ml: heroContentMargins.ml,
                    mr: heroContentMargins.mr,
                    textAlign: heroTextAlign,
                    color: "#fff",
                  }}
                >
                  {hero?.eyebrow ? (
                    <Typography
                      data-landing-field="hero.eyebrow"
                      variant="overline"
                      sx={{
                        ...safeTextSx,
                        display: "block",
                        maxWidth: 820,
                        ml: heroContentMargins.ml,
                        mr: heroContentMargins.mr,
                        mb: 1,
                        color: "rgba(255,255,255,.78)",
                        fontSize: eyebrowSize,
                        textShadow: "0 2px 10px rgba(0,0,0,.45)",
                        lineHeight: 1.45,
                      }}
                    >
                      {hero.eyebrow}
                    </Typography>
                  ) : null}

                  {title || rotatingPhrase ? (
                    <Box sx={{ minWidth: 0, maxWidth: "100%", mb: 1.5 }}>
                      {title ? (
                        <Typography
                          data-landing-field={titleFieldPath}
                          component="h1"
                          variant="h1"
                          sx={{
                            ...safeTitleSx,
                            width: "100%",
                            maxWidth: 1080,
                            ml: heroContentMargins.ml,
                            mr: heroContentMargins.mr,
                            fontSize: titleSize,
                            lineHeight: { xs: 1.02, sm: 1.04, md: 1.05 },
                            fontWeight: "var(--wp-segment-title-weight)",
                            letterSpacing: "var(--wp-segment-letter-spacing)",
                            color: "#fff",
                            textShadow: "0 4px 24px rgba(0,0,0,.62)",
                            filter: "none",
                          }}
                        >
                          {title}
                        </Typography>
                      ) : null}

                      {rotatingPhrase ? (
                        <Typography
                          data-landing-field={rotatingFieldPath}
                          component="div"
                          variant="h2"
                          sx={{
                            ...safeTitleSx,
                            width: "100%",
                            maxWidth: 980,
                            ml: heroContentMargins.ml,
                            mr: heroContentMargins.mr,
                            mt: 0.4,
                            color: "#fff",
                            fontSize: phraseSize,
                            fontWeight: "var(--wp-segment-title-weight)",
                            letterSpacing: "var(--wp-segment-letter-spacing)",
                            lineHeight: 1.08,
                            textShadow: "0 4px 22px rgba(0,0,0,.68)",
                            opacity: phase === "visible" ? 1 : 0,
                            transform:
                              phase === "exit"
                                ? "translateX(-56px)"
                                : phase === "enter"
                                  ? "translateX(56px)"
                                  : "translateX(0)",
                            transition:
                              phase === "enter"
                                ? "none"
                                : "opacity .38s ease, transform .38s ease",
                          }}
                        >
                          {rotatingPhrase}
                        </Typography>
                      ) : null}
                    </Box>
                  ) : null}

                  {subtitle ? (
                    <Typography
                      data-landing-field={subtitleFieldPath}
                      sx={{
                        ...safeTextSx,
                        maxWidth: subtitleIsVeryLong ? 880 : 760,
                        ml: heroContentMargins.ml,
                        mr: heroContentMargins.mr,
                        mb: { xs: 2.4, sm: 3.5 },
                        color: "rgba(255,255,255,.88)",
                        fontSize: subtitleSize,
                        lineHeight: subtitleIsVeryLong ? 1.55 : 1.6,
                        textShadow: "0 2px 10px rgba(0,0,0,.55)",
                        whiteSpace: "pre-line",
                      }}
                    >
                      {subtitle}
                    </Typography>
                  ) : null}

                  {showPrimaryCta || showSecondaryCta ? (
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent={heroContentJustify}
                      alignItems="stretch"
                      spacing={1.5}
                      sx={{
                        width: "100%",
                        maxWidth: { xs: 390, sm: 760 },
                        minWidth: 0,
                        ml: heroContentMargins.ml,
                        mr: heroContentMargins.mr,
                        "& > *": { minWidth: 0 },
                      }}
                    >
                      {showPrimaryCta ? (
                        <HeroButton
                          label={hero?.primaryCtaLabel}
                          url={hero?.primaryCtaUrl}
                          icon={hero?.primaryCtaShowIcon !== false ? <HeroCtaIcon name={hero?.primaryCtaIcon} fallback="explore" /> : undefined}
                          fieldPath="hero.primaryCtaLabel"
                          style={hero?.primaryCtaStyle}
                          colors={heroColors}
                        />
                      ) : null}
                      {showSecondaryCta ? (
                        <HeroButton
                          label={secondaryCtaLabel}
                          url={secondaryCtaUrl}
                          outlined
                          icon={hero?.secondaryCtaShowIcon !== false ? <HeroCtaIcon name={hero?.secondaryCtaIcon} fallback="storefront" /> : undefined}
                          fieldPath="hero.secondaryCtaLabel"
                          style={hero?.secondaryCtaStyle}
                          colors={heroColors}
                        />
                      ) : null}
                    </Stack>
                  ) : null}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Carousel>
    </Box>
  );
}
